import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sparkles, Users, Camera, Plus, ImagePlus, Trash2, Clock, Tag, Palette,
  UserCircle2, Search, X, LayoutGrid, ScrollText, MapPin, FileText,
} from "lucide-react";
import { toast } from "sonner";

// Continuity Book · v2 · project-agnostic (film · ad_film · tv_* · web
// series · short film · event · micro drama).
// Two views: "Book" (per-character cards) and "Board" (chronological
// cross-character feed for the AD). Editable by PH owner + admin +
// AD/costume/hair/makeup/set/art/props/direction crew.
// Viewable by full crew + assigned actor.
export default function ContinuityBook({ projectId, canEditHint = true }) {
  const [view, setView]       = useState("book");   // "book" | "board"
  const [cards, setCards]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal]     = useState(0);
  const [win, setWin]         = useState(30);
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/projects/${projectId}/continuity-cards`);
      setCards(data.cards || []);
      setTotal(data.total || 0);
      setWin(data.window_days || 30);
    } catch (err) {
      // 403 = viewer without access — treat as empty book; render CTA only.
      if (err.response?.status !== 403) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail)
                     || "Could not load continuity");
      }
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [projectId]);

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="continuity-book">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
            Continuity Book
          </div>
          <h3 className="font-display text-2xl uppercase tracking-tight mt-1">
            {total > 0
              ? `${total} character${total > 1 ? "s" : ""} tracked`
              : "Track every look, every day"}
          </h3>
          <div className="mt-1 text-xs text-white/55">
            Costume · makeup · hair · props. Last {win} days of snapshots surface for reshoot recovery.
          </div>
        </div>
        {canEditHint && view === "book" && (
          <Button
            onClick={() => setNewOpen(true)}
            className="btn-themed h-11 px-5 rounded-full text-xs uppercase tracking-[0.25em]"
            data-testid="continuity-add-btn"
          >
            <Plus size={13} className="mr-1.5" /> New character
          </Button>
        )}
      </div>

      {/* View toggle — Book (per-character) vs Board (chrono feed) */}
      <div className="flex gap-2 mb-4" data-testid="continuity-view-toggle">
        <ViewToggleBtn active={view === "book"} onClick={() => setView("book")}
                       Icon={LayoutGrid} label="Book" testId="continuity-view-book" />
        <ViewToggleBtn active={view === "board"} onClick={() => setView("board")}
                       Icon={ScrollText} label="Board" testId="continuity-view-board" />
      </div>

      {view === "board" ? (
        <ContinuityBoard projectId={projectId} onOpenCard={(cardId) => {
          const c = cards.find((x) => x.id === cardId);
          if (c) setDetail(c);
        }} />
      ) : loading ? (
        <div className="text-sm text-white/45 py-8 text-center">Loading…</div>
      ) : cards.length === 0 ? (
        <div className="text-sm text-white/45 py-10 text-center"
             data-testid="continuity-empty">
          No continuity cards yet. Add your first character to lock the look.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
             data-testid="continuity-grid">
          {cards.map((c) => (
            <CardTile key={c.id} card={c}
                      projectId={projectId}
                      onOpen={() => setDetail(c)} />
          ))}
        </div>
      )}

      <NewCardDialog open={newOpen} onOpenChange={setNewOpen}
                     projectId={projectId} onSaved={load} />

      {detail && (
        <CardDetailDialog card={detail}
                          projectId={projectId}
                          canEdit={canEditHint}
                          onClose={() => setDetail(null)}
                          onChanged={load} />
      )}
    </div>
  );
}


function ViewToggleBtn({ active, onClick, Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={"h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-2 border transition-colors " +
        (active
          ? "border-[color:var(--association-theme)] bg-[color:var(--association-theme)]/10 text-white"
          : "border-white/15 text-white/60 hover:border-white/30 hover:text-white")}
    >
      <Icon size={12} /> {label}
    </button>
  );
}


// ─────────────────────── CONTINUITY BOARD ──────────────────────────
// Cross-character chronological feed. Filterable by scene + location
// + focus (kind). Powered by GET /projects/:id/continuity-board.
function ContinuityBoard({ projectId, onOpenCard }) {
  const [feed, setFeed]       = useState([]);
  const [facets, setFacets]   = useState({ scenes: [], locations: [] });
  const [scene, setScene]     = useState("");
  const [location, setLocation] = useState("");
  const [kind, setKind]       = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    const params = new URLSearchParams();
    if (scene) params.set("scene", scene);
    if (location) params.set("location", location);
    if (kind) params.set("kind", kind);
    setLoading(true);
    api.get(`/projects/${projectId}/continuity-board?${params.toString()}`)
      .then(({ data }) => {
        if (dead) return;
        setFeed(data.snapshots || []);
        setFacets(data.facets || { scenes: [], locations: [] });
      })
      .catch((err) => {
        if (err.response?.status !== 403) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail)
                       || "Could not load board");
        }
        setFeed([]);
      })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [projectId, scene, location, kind]);

  return (
    <div className="space-y-4" data-testid="continuity-board-view">
      <div className="flex flex-wrap gap-2" data-testid="continuity-board-filters">
        <select value={scene}
                onChange={(e) => setScene(e.target.value)}
                data-testid="continuity-board-scene"
                className="h-9 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] bg-black/40 border border-white/15 text-white">
          <option value="">All scenes</option>
          {facets.scenes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={location}
                onChange={(e) => setLocation(e.target.value)}
                data-testid="continuity-board-location"
                className="h-9 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] bg-black/40 border border-white/15 text-white">
          <option value="">All locations</option>
          {facets.locations.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <div className="flex gap-1" data-testid="continuity-board-kind">
          {["", "full", "costume", "makeup", "hair", "props"].map((k) => (
            <button key={k || "any"} type="button"
                    onClick={() => setKind(k)}
                    className={"h-9 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] border transition-colors " +
                      (kind === k
                        ? "border-[color:var(--association-theme)] bg-[color:var(--association-theme)]/10 text-white"
                        : "border-white/15 text-white/55 hover:border-white/30 hover:text-white")}
                    data-testid={`continuity-board-kind-${k || "any"}`}>
              {k || "Any"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-white/45 py-8 text-center">Loading feed…</div>
      ) : feed.length === 0 ? (
        <div className="text-sm text-white/45 py-10 text-center"
             data-testid="continuity-board-empty">
          No snapshots match. Try clearing the filters.
        </div>
      ) : (
        <div className="space-y-3" data-testid="continuity-board-feed">
          {feed.map((s) => (
            <BoardRow key={s.id} snap={s} projectId={projectId}
                      onOpenCard={() => onOpenCard(s.card_id)} />
          ))}
        </div>
      )}
    </div>
  );
}


function BoardRow({ snap, projectId, onOpenCard }) {
  const photos = snap.photos || [];
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
         data-testid={`continuity-board-row-${snap.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <button onClick={onOpenCard}
                className="text-left"
                data-testid={`continuity-board-open-card-${snap.card_id}`}>
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">
            {snap.actor_name || "—"}
          </div>
          <div className="text-sm font-semibold text-white hover:underline">
            {snap.character_name || "—"}
          </div>
        </button>
        <div className="ml-auto flex items-center gap-2 flex-wrap text-[10px] tracking-[0.22em] uppercase text-white/55">
          <span className="px-1.5 py-0.5 rounded bg-white/8 text-white/70">
            {snap.kind || "full"}
          </span>
          {snap.scene_ref && (
            <span className="inline-flex items-center gap-1">
              <FileText size={10} /> Sc {snap.scene_ref}
            </span>
          )}
          {snap.day_ref && <span>Day {snap.day_ref}</span>}
          {snap.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={10} /> {snap.location}
            </span>
          )}
          <span className="text-white/40">
            {(snap.taken_at || "").slice(0, 16).replace("T", " ")}
          </span>
        </div>
      </div>
      {snap.notes && (
        <div className="mt-2 text-sm text-white/80 whitespace-pre-line line-clamp-3">
          {snap.notes}
        </div>
      )}
      {photos.length > 0 && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
          {photos.slice(0, 6).map((p) => (
            <div key={p.id} className="relative">
              <ContinuityImg projectId={projectId} cardId={snap.card_id}
                             refUrl={p.url} alt={p.caption || "snap"}
                             className="w-full aspect-square object-cover rounded-lg border border-white/10" />
              {p.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-black/70 text-[9px] text-white/85 px-1.5 py-1 truncate">
                  {p.caption}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}





// ────────────────────────── CARD TILE ────────────────────────────
function CardTile({ card, projectId, onOpen }) {
  const snaps = card.snapshots || [];
  return (
    <button
      onClick={onOpen}
      className="rounded-xl border border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06] transition-colors text-left overflow-hidden group"
      data-testid={`continuity-card-${card.id}`}
    >
      <div className="aspect-[4/3] bg-black/40 border-b border-white/10 relative overflow-hidden">
        {card.hero_photo_url ? (
          <ContinuityImg projectId={projectId} cardId={card.id}
                         refUrl={card.hero_photo_url}
                         alt={`${card.character_name} continuity photo`}
                         className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/25">
            <UserCircle2 size={48} />
          </div>
        )}
        {card.status === "wrapped" && (
          <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.22em] bg-black/60 text-white/80">
            Wrapped
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
          {card.actor_name || "Unnamed cast"}
        </div>
        <h4 className="font-display text-lg uppercase tracking-tight mt-0.5 text-white">
          {card.character_name}
        </h4>
        <div className="mt-2 flex items-center gap-3 text-[10px] tracking-[0.22em] uppercase text-white/50">
          <span className="inline-flex items-center gap-1">
            <Clock size={11} /> {snaps.length} snap{snaps.length === 1 ? "" : "s"}
          </span>
          {card.actor_user_id && (
            <span className="inline-flex items-center gap-1 text-emerald-300/85">
              <Tag size={11} /> Actor linked
            </span>
          )}
        </div>
      </div>
    </button>
  );
}


// ────────────────────────── NEW CARD ────────────────────────────
function NewCardDialog({ open, onOpenChange, projectId, onSaved }) {
  const [charName, setCharName] = useState("");
  const [actorName, setActorName] = useState("");
  const [actorUserId, setActorUserId] = useState("");
  const [actorSearchLabel, setActorSearchLabel] = useState("");
  const [costume, setCostume] = useState("");
  const [makeup, setMakeup] = useState("");
  const [hair, setHair]     = useState("");
  const [props_, setProps]  = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCharName(""); setActorName(""); setActorUserId("");
    setActorSearchLabel("");
    setCostume(""); setMakeup(""); setHair(""); setProps("");
  };

  const submit = async () => {
    if (!charName.trim() || !actorName.trim()) {
      toast.error("Character name and actor name are required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/continuity-cards`, {
        character_name: charName.trim(),
        actor_name:     actorName.trim(),
        actor_user_id:  actorUserId || null,
        costume_notes:  costume.trim(),
        makeup_notes:   makeup.trim(),
        hair_notes:     hair.trim(),
        props_notes:    props_.trim(),
      });
      toast.success("Continuity card created");
      onOpenChange(false);
      reset();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
                   || "Could not create");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open}
            onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto"
                     data-testid="continuity-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            New continuity card
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Lock the look for one character. Add snapshots later as the shoot progresses.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Character name *
              </Label>
              <Input value={charName} onChange={(e) => setCharName(e.target.value)}
                     placeholder="e.g. Saraswati"
                     className="mt-2 h-11 bg-black/40 border-white/15"
                     data-testid="continuity-char-input" autoFocus />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Actor name *
              </Label>
              <Input value={actorName} onChange={(e) => setActorName(e.target.value)}
                     placeholder="e.g. Meera Kapoor"
                     className="mt-2 h-11 bg-black/40 border-white/15"
                     data-testid="continuity-actor-input" />
            </div>
          </div>

          <ActorLinkPicker
            selectedId={actorUserId}
            selectedLabel={actorSearchLabel}
            onSelect={(u) => {
              setActorUserId(u.id);
              setActorSearchLabel(u.name);
              if (!actorName.trim() && u.name) setActorName(u.name);
            }}
            onClear={() => { setActorUserId(""); setActorSearchLabel(""); }}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Costume notes
              </Label>
              <Textarea value={costume} onChange={(e) => setCostume(e.target.value)}
                        rows={3} className="mt-2 bg-black/40 border-white/15 text-sm"
                        data-testid="continuity-costume-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Makeup notes
              </Label>
              <Textarea value={makeup} onChange={(e) => setMakeup(e.target.value)}
                        rows={3} className="mt-2 bg-black/40 border-white/15 text-sm"
                        data-testid="continuity-makeup-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Hair notes
              </Label>
              <Textarea value={hair} onChange={(e) => setHair(e.target.value)}
                        rows={3} className="mt-2 bg-black/40 border-white/15 text-sm"
                        data-testid="continuity-hair-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Props / accessories
              </Label>
              <Textarea value={props_} onChange={(e) => setProps(e.target.value)}
                        rows={3} className="mt-2 bg-black/40 border-white/15 text-sm"
                        data-testid="continuity-props-input" />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}
                    className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
                    data-testid="continuity-new-cancel">
              Cancel
            </Button>
            <Button onClick={submit}
                    disabled={saving || !charName.trim() || !actorName.trim()}
                    className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
                    data-testid="continuity-new-submit">
              {saving ? "Saving…" : "Create card"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────── ACTOR PICKER ───────────────────────────
function ActorLinkPicker({ selectedId, selectedLabel, onSelect, onClear }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) { setResults([]); return; }
    let dead = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/crew/lookup?q=${encodeURIComponent(q)}`);
        if (!dead) setResults(data.results || []);
      } catch { /* silent */ }
      finally { if (!dead) setBusy(false); }
    }, 260);
    return () => { dead = true; clearTimeout(t); };
  }, [q]);

  return (
    <div>
      <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
        Link platform actor (optional)
      </Label>
      {selectedId ? (
        <div className="mt-2 h-11 px-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 flex items-center justify-between"
             data-testid="continuity-actor-linked">
          <span className="text-sm text-white flex items-center gap-2">
            <Tag size={12} className="text-emerald-300" />
            {selectedLabel || selectedId}
          </span>
          <button onClick={onClear}
                  className="text-white/40 hover:text-red-300 transition-colors"
                  data-testid="continuity-actor-unlink">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative mt-2">
          <div className="relative">
            <Search size={13}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Search platform users by name / phone / email"
              className="h-11 pl-9 bg-black/40 border-white/15"
              data-testid="continuity-actor-search"
            />
          </div>
          {open && q.trim().length >= 2 && (
            <div className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-white/15 bg-[#0a0a0c] shadow-xl max-h-64 overflow-y-auto"
                 data-testid="continuity-actor-results">
              {busy ? (
                <div className="p-3 text-xs text-white/50">Searching…</div>
              ) : results.length === 0 ? (
                <div className="p-3 text-xs text-white/50">No match.</div>
              ) : (
                results.map((u) => (
                  <button key={u.id}
                          type="button"
                          onClick={() => { onSelect(u); setOpen(false); setQ(""); }}
                          className="w-full text-left px-3 py-2 hover:bg-white/8 border-b border-white/5 last:border-0"
                          data-testid={`continuity-actor-result-${u.id}`}>
                    <div className="text-sm text-white">{u.name || "Unnamed"}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                      {u.role_label || u.primary_role || ""}
                      {u.phone ? ` · ${u.phone}` : ""}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─────────────────────── DETAIL DIALOG ──────────────────────────
function CardDetailDialog({ card, projectId, canEdit, onClose, onChanged }) {
  const [fresh, setFresh] = useState(card);
  const [snapOpen, setSnapOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    let dead = false;
    api.get(`/projects/${projectId}/continuity-cards/${card.id}`)
      .then(({ data }) => { if (!dead) setFresh(data.card); })
      .catch(() => {});
    return () => { dead = true; };
  }, [card.id, projectId]);

  const refresh = async () => {
    try {
      const { data } = await api.get(
        `/projects/${projectId}/continuity-cards/${card.id}`);
      setFresh(data.card);
      onChanged?.();
    } catch { /* ignore */ }
  };

  const withdraw = async () => {
    if (!confirm(`Delete the continuity card for ${fresh.character_name}? Snapshots are archived.`)) return;
    try {
      await api.delete(`/projects/${projectId}/continuity-cards/${card.id}`);
      toast.success("Card removed");
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
                   || "Could not remove");
    }
  };

  const setHero = async (photoRef) => {
    try {
      const { data } = await api.patch(
        `/projects/${projectId}/continuity-cards/${card.id}`,
        { hero_photo_url: photoRef });
      setFresh(data.card);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
                   || "Could not set hero");
    }
  };

  const snaps = fresh.snapshots || [];

  return (
    <>
      <Dialog open onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-4xl max-h-[92vh] overflow-y-auto"
                       data-testid={`continuity-detail-${card.id}`}>
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">
              {fresh.character_name}
            </DialogTitle>
            <DialogDescription className="text-white/55 text-xs">
              Played by {fresh.actor_name}
              {fresh.actor_user_id ? " · Linked to platform user" : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="grid md:grid-cols-[220px_1fr] gap-6 mt-4">
            <div>
              <div className="aspect-[4/5] rounded-xl overflow-hidden border border-white/10 bg-black/40">
                {fresh.hero_photo_url ? (
                  <ContinuityImg projectId={projectId} cardId={card.id}
                                 refUrl={fresh.hero_photo_url}
                                 alt="Hero look"
                                 className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/25">
                    <UserCircle2 size={64} />
                  </div>
                )}
              </div>
              {canEdit && (
                <div className="mt-3 space-y-2">
                  <PhotoUploader projectId={projectId} cardId={card.id}
                                 label="Set hero photo"
                                 onUploaded={setHero}
                                 testId="continuity-hero-upload" />
                  <Button variant="ghost"
                          onClick={() => setEditOpen(true)}
                          className="w-full h-9 text-[10px] uppercase tracking-[0.22em] border border-white/15 rounded-full"
                          data-testid="continuity-edit-btn">
                    Edit notes
                  </Button>
                  <button onClick={withdraw}
                          className="w-full text-[10px] uppercase tracking-[0.22em] text-white/40 hover:text-red-300 transition-colors py-2 flex items-center justify-center gap-1"
                          data-testid="continuity-delete-btn">
                    <Trash2 size={12} /> Delete card
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <NoteRow icon={<Palette size={12} />} label="Costume"
                       text={fresh.costume_notes} />
              <NoteRow icon={<Sparkles size={12} />} label="Makeup"
                       text={fresh.makeup_notes} />
              <NoteRow icon={<Users size={12} />} label="Hair"
                       text={fresh.hair_notes} />
              <NoteRow icon={<Camera size={12} />} label="Props"
                       text={fresh.props_notes} />

              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
                      Snapshots (last 30 days)
                    </div>
                    <div className="text-lg font-display uppercase tracking-tight mt-0.5">
                      {snaps.length} recent
                      {fresh.snapshot_total > snaps.length
                        ? ` · ${fresh.snapshot_total} total`
                        : ""}
                    </div>
                  </div>
                  {canEdit && (
                    <Button onClick={() => setSnapOpen(true)}
                            className="btn-themed h-10 px-4 rounded-full text-[10px] uppercase tracking-[0.22em]"
                            data-testid="continuity-snap-add-btn">
                      <Plus size={12} className="mr-1" /> Snapshot
                    </Button>
                  )}
                </div>
                {snaps.length === 0 ? (
                  <div className="text-sm text-white/45 py-6 text-center"
                       data-testid="continuity-snap-empty">
                    No snapshots yet in the window.
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="continuity-snap-list">
                    {snaps.map((s) => (
                      <SnapshotRow key={s.id} snap={s}
                                   projectId={projectId} cardId={card.id}
                                   canEdit={canEdit}
                                   onRemoved={refresh} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {snapOpen && (
        <NewSnapshotDialog card={fresh} projectId={projectId}
                           open={snapOpen}
                           onOpenChange={setSnapOpen}
                           onSaved={refresh} />
      )}
      {editOpen && (
        <EditCardDialog card={fresh} projectId={projectId}
                        open={editOpen}
                        onOpenChange={setEditOpen}
                        onSaved={refresh} />
      )}
    </>
  );
}


function NoteRow({ icon, label, text }) {
  if (!text) return (
    <div className="rounded-lg border border-white/8 bg-white/[0.02] p-3">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-white/45">
        {icon} {label}
      </div>
      <div className="text-xs text-white/35 mt-1">Not set yet.</div>
    </div>
  );
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-white/60">
        {icon} {label}
      </div>
      <div className="text-sm text-white/80 mt-1 whitespace-pre-line">
        {text}
      </div>
    </div>
  );
}


function SnapshotRow({ snap, projectId, cardId, canEdit, onRemoved }) {
  const [expanded, setExpanded] = useState(false);
  const remove = async () => {
    if (!confirm("Delete this snapshot?")) return;
    try {
      await api.delete(
        `/projects/${projectId}/continuity-cards/${cardId}/snapshots/${snap.id}`);
      toast.success("Snapshot removed");
      onRemoved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
                   || "Could not remove");
    }
  };
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3"
         data-testid={`continuity-snap-${snap.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.22em] bg-white/8 text-white/70">
            {snap.kind}
          </span>
          {snap.scene_ref && (
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/55 inline-flex items-center gap-1">
              <FileText size={10} /> Sc {snap.scene_ref}
            </span>
          )}
          {snap.day_ref && (
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Day {snap.day_ref}
            </span>
          )}
          {snap.location && (
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/55 inline-flex items-center gap-1">
              <MapPin size={10} /> {snap.location}
            </span>
          )}
          {snap.version && (
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              v{snap.version}
            </span>
          )}
          <span className="text-[10px] text-white/40">
            {snap.taken_by_name || "—"} · {(snap.taken_at || "").slice(0, 16).replace("T", " ")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setExpanded((e) => !e)}
                  className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-white transition-colors"
                  data-testid={`continuity-snap-toggle-${snap.id}`}>
            {expanded ? "Hide" : "Details"}
          </button>
          {canEdit && (
            <button onClick={remove}
                    className="text-white/40 hover:text-red-300 transition-colors"
                    data-testid={`continuity-snap-del-${snap.id}`}>
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="mt-3 space-y-2">
          {snap.notes && (
            <div className="text-sm text-white/80 whitespace-pre-line">
              {snap.notes}
            </div>
          )}
          {(snap.photos || []).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {snap.photos.map((p) => (
                <div key={p.id || p.url} className="relative rounded-lg overflow-hidden border border-white/10">
                  <SnapPhoto projectId={projectId} cardId={cardId} refUrl={p.url} />
                  {p.caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-black/70 text-[10px] text-white/85 px-2 py-1 truncate">
                      {p.caption}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function SnapPhoto({ projectId, cardId, refUrl }) {
  return (
    <ContinuityImg projectId={projectId} cardId={cardId} refUrl={refUrl}
                   alt="Continuity snapshot"
                   className="w-full aspect-square object-cover rounded-lg border border-white/10" />
  );
}


// ────────────────────── NEW SNAPSHOT ─────────────────────────────
function NewSnapshotDialog({ card, projectId, open, onOpenChange, onSaved }) {
  const [scene, setScene] = useState("");
  const [day, setDay]     = useState("");
  const [locationTxt, setLocationTxt] = useState("");
  const [version, setVersion] = useState("");
  const [kind, setKind]   = useState("full");
  const [notes, setNotes] = useState("");
  // Each entry is {url, caption}. Photos captured from mobile camera
  // or picked from the gallery get an empty caption the AD/HOD then
  // fills below the thumbnail.
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setScene(""); setDay(""); setLocationTxt(""); setVersion("");
    setKind("full"); setNotes(""); setPhotos([]);
  };

  const setCaption = (idx, val) => {
    setPhotos((prev) => prev.map((p, i) =>
      i === idx ? { ...p, caption: val } : p));
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(
        `/projects/${projectId}/continuity-cards/${card.id}/snapshots`, {
          scene_ref: scene.trim(),
          day_ref:   day.trim(),
          location:  locationTxt.trim(),
          version:   version.trim(),
          kind, notes: notes.trim(),
          photos:    photos.map((p) => ({
                       url:     p.url,
                       caption: (p.caption || "").trim(),
                     })),
        });
      toast.success("Snapshot logged");
      onOpenChange(false); reset();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
                   || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open}
            onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-xl max-h-[92vh] overflow-y-auto"
                     data-testid="continuity-snap-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            Log a snapshot
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Stamp the current look for reshoots and next-day continuity.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Scene ref
              </Label>
              <Input value={scene} onChange={(e) => setScene(e.target.value)}
                     placeholder="Sc 12A"
                     className="mt-2 h-11 bg-black/40 border-white/15"
                     data-testid="continuity-snap-scene" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Shoot day
              </Label>
              <Input value={day} onChange={(e) => setDay(e.target.value)}
                     placeholder="Day 07"
                     className="mt-2 h-11 bg-black/40 border-white/15"
                     data-testid="continuity-snap-day" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Version
              </Label>
              <Input value={version} onChange={(e) => setVersion(e.target.value)}
                     placeholder="v2"
                     className="mt-2 h-11 bg-black/40 border-white/15"
                     data-testid="continuity-snap-version" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Location
            </Label>
            <Input value={locationTxt}
                   onChange={(e) => setLocationTxt(e.target.value)}
                   placeholder="e.g. Filmistan floor 3 · haveli living room"
                   className="mt-2 h-11 bg-black/40 border-white/15"
                   data-testid="continuity-snap-location" />
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Focus
            </Label>
            <div className="mt-2 grid grid-cols-5 gap-2" data-testid="continuity-snap-kind">
              {["full", "costume", "makeup", "hair", "props"].map((k) => (
                <button key={k} type="button"
                        onClick={() => setKind(k)}
                        className={"px-2 py-2 text-[10px] tracking-[0.22em] uppercase rounded-lg border transition-colors " +
                          (kind === k
                            ? "border-[color:var(--association-theme)] bg-[color:var(--association-theme)]/10 text-white"
                            : "border-white/15 text-white/60 hover:border-white/30 hover:text-white")}
                        data-testid={`continuity-snap-kind-${k}`}>
                  {k}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Notes
            </Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={4} placeholder="Watch for: red bindi centered, bangles on right wrist only, saree pallu falling left."
                      className="mt-2 bg-black/40 border-white/15 text-sm"
                      data-testid="continuity-snap-notes" />
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Photos ({photos.length})
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-3"
                 data-testid="continuity-snap-photo-list">
              {photos.map((p, i) => (
                <div key={i}
                     className="rounded-lg border border-white/10 overflow-hidden bg-white/[0.02]"
                     data-testid={`continuity-snap-photo-item-${i}`}>
                  <div className="aspect-square relative">
                    <SnapPhoto projectId={projectId} cardId={card.id}
                               refUrl={p.url} />
                    <button type="button"
                            onClick={() => setPhotos(photos.filter((_, ix) => ix !== i))}
                            className="absolute top-1 right-1 bg-black/70 rounded-full p-1 text-white/70 hover:text-red-300"
                            data-testid={`continuity-snap-photo-remove-${i}`}>
                      <X size={11} />
                    </button>
                  </div>
                  <Input
                    value={p.caption || ""}
                    onChange={(e) => setCaption(i, e.target.value)}
                    placeholder="What this is for…"
                    className="h-9 border-0 rounded-none text-xs bg-black/30 border-t border-white/8"
                    data-testid={`continuity-snap-photo-caption-${i}`}
                  />
                </div>
              ))}
              <div className="aspect-square">
                <PhotoUploader
                  projectId={projectId} cardId={card.id}
                  label="Camera / gallery"
                  small
                  captureCamera
                  onUploaded={(ref) => setPhotos((prev) => [
                    ...prev, { url: ref, caption: "" }])}
                  testId="continuity-snap-photo-upload"
                />
              </div>
            </div>
            <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-white/40">
              Tip · on mobile the button opens your back camera directly.
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}
                    className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
                    data-testid="continuity-snap-cancel">
              Cancel
            </Button>
            <Button onClick={submit}
                    disabled={saving}
                    className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
                    data-testid="continuity-snap-submit">
              {saving ? "Saving…" : "Log snapshot"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ────────────────────── EDIT CARD ─────────────────────────────
function EditCardDialog({ card, projectId, open, onOpenChange, onSaved }) {
  const [charName, setCharName] = useState(card.character_name || "");
  const [actorName, setActorName] = useState(card.actor_name || "");
  const [costume, setCostume] = useState(card.costume_notes || "");
  const [makeup, setMakeup]   = useState(card.makeup_notes || "");
  const [hair, setHair]       = useState(card.hair_notes || "");
  const [props_, setProps]    = useState(card.props_notes || "");
  const [status, setStatus]   = useState(card.status || "active");
  const [saving, setSaving]   = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(
        `/projects/${projectId}/continuity-cards/${card.id}`, {
          character_name: charName.trim(),
          actor_name:     actorName.trim(),
          costume_notes:  costume.trim(),
          makeup_notes:   makeup.trim(),
          hair_notes:     hair.trim(),
          props_notes:    props_.trim(),
          status,
        });
      toast.success("Card updated");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
                   || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto"
                     data-testid="continuity-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            Edit continuity card
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Update character/actor names, the look notes, or wrap the card.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Character *</Label>
              <Input value={charName} onChange={(e) => setCharName(e.target.value)}
                     className="mt-2 h-11 bg-black/40 border-white/15"
                     data-testid="continuity-edit-char" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Actor *</Label>
              <Input value={actorName} onChange={(e) => setActorName(e.target.value)}
                     className="mt-2 h-11 bg-black/40 border-white/15"
                     data-testid="continuity-edit-actor" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Costume</Label>
              <Textarea value={costume} onChange={(e) => setCostume(e.target.value)}
                        rows={3} className="mt-2 bg-black/40 border-white/15 text-sm" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Makeup</Label>
              <Textarea value={makeup} onChange={(e) => setMakeup(e.target.value)}
                        rows={3} className="mt-2 bg-black/40 border-white/15 text-sm" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Hair</Label>
              <Textarea value={hair} onChange={(e) => setHair(e.target.value)}
                        rows={3} className="mt-2 bg-black/40 border-white/15 text-sm" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Props</Label>
              <Textarea value={props_} onChange={(e) => setProps(e.target.value)}
                        rows={3} className="mt-2 bg-black/40 border-white/15 text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Status</Label>
            <div className="mt-2 flex gap-2">
              {["active", "wrapped"].map((s) => (
                <button key={s} type="button"
                        onClick={() => setStatus(s)}
                        className={"px-4 py-2 text-[10px] tracking-[0.22em] uppercase rounded-full border transition-colors " +
                          (status === s
                            ? "border-[color:var(--association-theme)] bg-[color:var(--association-theme)]/10 text-white"
                            : "border-white/15 text-white/60 hover:border-white/30")}
                        data-testid={`continuity-edit-status-${s}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}
                    className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
                    data-testid="continuity-edit-cancel">
              Cancel
            </Button>
            <Button onClick={submit}
                    disabled={saving || !charName.trim() || !actorName.trim()}
                    className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
                    data-testid="continuity-edit-submit">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ────────────────────── PHOTO UPLOADER ────────────────────────────
function PhotoUploader({ projectId, cardId, label, onUploaded, testId, small,
                        captureCamera }) {
  const [busy, setBusy] = useState(false);
  const inputId = `${testId}-file`;
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo exceeds 5 MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/projects/${projectId}/continuity-cards/${cardId}/photos`, form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onUploaded?.(data.photo);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
                   || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  // `capture="environment"` cues mobile browsers to open the back camera
  // directly. Desktop browsers fall back to the file picker.
  const captureAttr = captureCamera ? "environment" : undefined;
  if (small) {
    return (
      <label htmlFor={inputId}
             className="w-full h-full rounded-lg border border-dashed border-white/20 hover:border-white/40 flex flex-col items-center justify-center cursor-pointer text-white/45 hover:text-white transition-colors gap-1"
             data-testid={testId}>
        {busy ? "…" : (
          <>
            <Camera size={20} />
            <span className="text-[9px] tracking-[0.22em] uppercase">
              {label || "Add"}
            </span>
          </>
        )}
        <input id={inputId} type="file"
               accept="image/jpeg,image/png,image/webp,image/gif"
               capture={captureAttr}
               onChange={handle} className="hidden" />
      </label>
    );
  }
  return (
    <label htmlFor={inputId}
           className="w-full h-9 rounded-full border border-white/20 hover:border-white/40 flex items-center justify-center gap-1.5 cursor-pointer text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-white transition-colors"
           data-testid={testId}>
      <ImagePlus size={12} />
      {busy ? "Uploading…" : (label || "Upload")}
      <input id={inputId} type="file"
             accept="image/jpeg,image/png,image/webp,image/gif"
             capture={captureAttr}
             onChange={handle} className="hidden" />
    </label>
  );
}


// ─────────────────── AUTHED PHOTO COMPONENT ────────────────────────
// Fetches the storage bytes via axios (Bearer token), turns them into
// a blob URL, and renders as <img>. Revokes on unmount. Falls back to
// external URLs (non-`emergent:`) untouched.
function ContinuityImg({ projectId, cardId, refUrl, alt, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    if (!refUrl) { setSrc(null); return; }
    if (!refUrl.startsWith("emergent:")) {
      setSrc(refUrl);
      return;
    }
    api.get(
      `/projects/${projectId}/continuity-cards/${cardId}/photos/serve`,
      { params: { path: refUrl }, responseType: "blob" },
    ).then(({ data }) => {
      if (!alive) return;
      objectUrl = URL.createObjectURL(data);
      setSrc(objectUrl);
    }).catch(() => { if (alive) setSrc(null); });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [projectId, cardId, refUrl]);
  if (!src) return null;
  return <img src={src} alt={alt || "Continuity"} className={className} />;
}

